import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Form } from "antd";
import dayjs from "dayjs";
import TextField from "@/common/elements/InputElements/TextField";
import Select from "@/common/elements/InputElements/Select";
import SecureFormItem from "@/common/SecureFormItem";
import DynamicForm from "@/common/DynamicForm";
import { StyledGridCol, StyledGridRow, StyledSectionHeading } from "@/common/styles";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import { AliasesList } from "@/components/BookingsDetails/PersonalInformationDetails/AliasesList";
import { AddressesList } from "@/components/BookingsDetails/PersonalInformationDetails/AddressesList";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";
import {
  getOptionsFromRefList,
  convertApiFields,
  getSQLTypeToHTMLType,
  isNonEmptyVariable,
} from "@/util/common_function";
import {
  fetchExtraTableRecord,
  saveExtraTableRecord,
} from "@/util/extraColumnActions";

// mode: "add" | "modify" | "detail" — "detail" renders the form read-only.
// Exposes validateAndGetPayload() for the parent (InterestedParties.jsx)'s
// OK button — the affiant record (including its bio/aliases/addresses) is
// only persisted when the whole Warrant is saved (activeWarrant.
// warrantAffiant), same "deferred until the aggregate save" model as
// WorkCrew's Roster/BlackList entries. WarrantAffiantExtra is the one
// exception — it's saved immediately here when editing an already-saved
// affiant (a real warrantAffiantID), same reasoning as every other
// list-child extra table in this app: a brand new affiant only gets that
// id once the whole Warrant is saved, which this modal doesn't wait for.
const WarrantAffiantForm = forwardRef(
  ({ initialValues = null, disabled = false, startingPoint = "Warrants" }, ref) => {
    const [form] = Form.useForm();
    const [aliases, setAliases] = useState([]);
    const [addresses, setAddresses] = useState([]);

    // -----------------------------------------------------------------------
    // WarrantAffiantExtra — PCI's per-client dynamic "_U" columns for this
    // affiant (WarrantAffiantInterface.asp's "Additional Affiant
    // Information" section).
    // -----------------------------------------------------------------------
    const [affiantExtraFields, setAffiantExtraFields] = useState([]);
    const [affiantExtraFieldsReady, setAffiantExtraFieldsReady] = useState(false);
    const affiantExtraTypeMapRef = useRef({});
    const affiantExtraIdRef = useRef(0);

    useEffect(() => {
      api
        .get(`${API_ENDPOINTS.getTableColumns}/WarrantAffiantExtra`)
        .then((response) => {
          const tableData = (response?.data || []).filter((d) => d?.columnName?.endsWith("_U"));
          const converted = convertApiFields(tableData, "WarrantAffiantExtra");
          const typeMap = {};
          converted.forEach((c) => { typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type); });
          affiantExtraTypeMapRef.current = typeMap;
          setAffiantExtraFields(converted);
          setAffiantExtraFieldsReady(true);
        })
        .catch((error) => {
          console.error("[WarrantAffiantForm] WarrantAffiantExtra field defs fetch failed:", error);
        });
    }, []);

    // Fetches the saved extra row once a real warrantAffiantID is present
    // (edit/view mode) — a brand new affiant has no id yet.
    useEffect(() => {
      if (!affiantExtraFieldsReady) return;
      const id = initialValues?.warrantAffiantID;
      if (!isNonEmptyVariable(id)) {
        affiantExtraIdRef.current = 0;
        return;
      }

      fetchExtraTableRecord("WarrantAffiantExtra", "warrantAffiantID", id)
        .then((result) => {
          if (!result) return;
          affiantExtraIdRef.current = result.raw?.WarrantAffiantExtraID ?? 0;
          const withDates = Object.keys(result.values).reduce((acc, key) => {
            const val = result.values[key];
            const htmlType = affiantExtraTypeMapRef.current[key];
            const isDateField = htmlType === "date" || htmlType === "datetime";
            acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
            return acc;
          }, {});
          form.setFieldsValue(withDates);
        })
        .catch((error) => {
          console.error("[WarrantAffiantForm] WarrantAffiantExtra fetch failed:", error);
        });
    }, [initialValues?.warrantAffiantID, affiantExtraFieldsReady]);

    useEffect(() => {
      const bio = initialValues?.bio?.[0];
      if (bio) {
        form.setFieldsValue({
          lName: bio.lName,
          fName: bio.fName,
          mName: bio.mName,
          suffix: bio.suffix,
          daytimePhone: bio.daytimePhone,
          phone: bio.phone,
        });
        setAliases(bio.bioAliases || []);
        setAddresses(bio.bioAddresses || []);
      } else {
        form.resetFields();
        setAliases([]);
        setAddresses([]);
      }
    }, [initialValues, form]);

    useImperativeHandle(ref, () => ({
      validateAndGetPayload: () =>
        form.validateFields().then((values) => {
          // Split WarrantAffiantExtra's dynamic fields out of the form
          // values — saved separately below, not folded into the bio-level
          // payload this modal hands back to InterestedParties.jsx.
          const affiantExtraFieldKeys = affiantExtraFields.map((f) => f.columnName);
          const affiantExtraSnapshot = {};
          affiantExtraFieldKeys.forEach((k) => {
            if (k in values) {
              const val = values[k];
              const htmlType = affiantExtraTypeMapRef.current[k];
              affiantExtraSnapshot[k] = dayjs.isDayjs(val)
                ? val.format(htmlType === "datetime" ? "MM/DD/YYYY HH:mm" : "MM/DD/YYYY")
                : val;
              delete values[k];
            }
          });

          const existingAffiantId = initialValues?.warrantAffiantID;
          if (affiantExtraFieldKeys.length > 0 && isNonEmptyVariable(existingAffiantId)) {
            saveExtraTableRecord({
              tableName: "WarrantAffiantExtra",
              idColumn: "warrantAffiantID",
              idValue: existingAffiantId,
              extraRecord: {
                ...affiantExtraSnapshot,
                warrantAffiantExtraID: affiantExtraIdRef.current || undefined,
              },
              extraIdKey: "warrantAffiantExtraID",
            }).catch((error) => {
              console.error("[WarrantAffiantForm] WarrantAffiantExtra save failed:", error);
            });
          }

          return {
            warrantAffiantID: initialValues?.warrantAffiantID ?? 0,
            warrantID: initialValues?.warrantID ?? 0,
            bioID: initialValues?.bioID ?? initialValues?.bio?.[0]?.bioId ?? null,
            jacketID: initialValues?.jacketID ?? null,
            dateAdded: initialValues?.dateAdded ?? null,
            timeAdded: initialValues?.timeAdded ?? null,
            bio: [
              {
                ...(initialValues?.bio?.[0] || {}),
                lName: values.lName,
                fName: values.fName,
                mName: values.mName,
                suffix: values.suffix,
                daytimePhone: values.daytimePhone,
                phone: values.phone,
                bioAliases: aliases,
                bioAddresses: addresses,
              },
            ],
          };
        }),
    }));

    return (
      <FieldSecurityProvider
        entity="WarrantAffiant"
        startingPoint={startingPoint}
        bookingsFieldsEnabled={!disabled}
      >
        <StyledSectionHeading>
          <span>Personal Information</span>
        </StyledSectionHeading>

        <Form form={form} disabled={disabled}>
          <StyledGridRow gutter={16}>
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="lName"
                formItemProps={{ name: "lName", label: "Last Name", rules: [{ required: true, message: "Last Name is required" }] }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="fName"
                formItemProps={{ name: "fName", label: "First Name" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="mName"
                formItemProps={{ name: "mName", label: "Middle Name" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="suffix"
                formItemProps={{ name: "suffix", label: "Suffix" }}
              >
                <Select options={getOptionsFromRefList("Suffix")} showSearch />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="daytimePhone"
                formItemProps={{ name: "daytimePhone", label: "Daytime Phone" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
            <StyledGridCol span={6}>
              <SecureFormItem
                fieldName="phone"
                formItemProps={{ name: "phone", label: "Evening Phone" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
          </StyledGridRow>

          <AliasesList
            associates
            selectedAliases={aliases}
            onAliasesChange={setAliases}
            startingPoint={startingPoint}
          />

          <AddressesList
            associates
            selectedAddresses={addresses}
            onAddressesChange={setAddresses}
            startingPoint={startingPoint}
          />

          {affiantExtraFields.length > 0 && (
            <>
              <StyledSectionHeading>
                <span>Additional Affiant Information</span>
              </StyledSectionHeading>
              <StyledGridRow gutter={16}>
                {affiantExtraFields.map((sf, index) => (
                  <DynamicForm
                    key={index}
                    formAttributes={sf}
                    form={form}
                    bookingsFieldsEnabled={!disabled}
                  />
                ))}
              </StyledGridRow>
            </>
          )}
        </Form>
      </FieldSecurityProvider>
    );
  },
);

WarrantAffiantForm.displayName = "WarrantAffiantForm";

export default WarrantAffiantForm;
